import React, { Component } from 'react';
import {
    Button,
    Col,
    Input,
    message,
    Modal,
    Popconfirm,
    Row,
    Table,
    Divider,
    DatePicker,
    Checkbox,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import UpdateEditBooking from './UpdateEditBooking';
import Cookies from 'js-cookie';
import { Redirect, withRouter } from 'react-router-dom';
import moment from 'dayjs';

class BookingView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            bookings: [],
            searchText: '',
            dateRange: [moment(), null],
            isUpdateBookingModalVisible: false,
            isUpdateBookingModalVisible: false,
            selectedBooking: null,
            dailyStatuses: {},
        };
    }

    componentDidMount() {
        this.getAllBookings();
        this.getDailyStatuses();
    }

    async getDailyStatuses() {
        try {
            const response = await axios.get('https://api.srilankaballooning.com/api/getDailyStatuses');
            if (response.data.success) {
                const statusMap = {};
                response.data.result.forEach(item => {
                    statusMap[item.DATE] = item.IS_CANCELLED === 1;
                });
                this.setState({ dailyStatuses: statusMap });
            }
        } catch (error) {
            console.error('Error fetching daily statuses:', error);
        }
    }

    handleStatusChange = async (date, checked) => {
        // Optimistic update
        const newStatuses = { ...this.state.dailyStatuses, [date]: checked };
        this.setState({ dailyStatuses: newStatuses });

        try {
            await axios.post('https://api.srilankaballooning.com/api/updateDailyStatus', {
                DATE: date,
                IS_CANCELLED: checked ? 1 : 0
            });
            message.success(`Flight status for ${date} updated.`);
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status.');
            // Revert on error
            const revertStatuses = { ...this.state.dailyStatuses, [date]: !checked };
            this.setState({ dailyStatuses: revertStatuses });
        }
    };

    async getAllBookings() {
        this.setState({ loading: true });
        try {
            const response = await axios.post('https://api.srilankaballooning.com/api/getAllBookings');
            if (response.data.success) {
                this.setState({ bookings: response.data.result });
            } else {
                message.error('Failed to fetch bookings.');
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            message.error('Internal server error');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleSearch = (e) => {
        this.setState({ searchText: e.target.value.toLowerCase() });
    };

    handleDateChange = (field, date) => {
        const newRange = [...this.state.dateRange];
        newRange[field === 'start' ? 0 : 1] = date;
        this.setState({ dateRange: newRange });
    };

    handleUpdateShow = (booking) => {
        this.setState({ selectedBooking: booking, isUpdateBookingModalVisible: true });
    };

    toggleUpdateBookingModal = () => {
        this.setState((prevState) => ({
            isUpdateBookingModalVisible: !prevState.isUpdateBookingModalVisible,
        }));
    };

    handleDelete = async (bookingId) => {
        try {
            const response = await axios.post('https://api.srilankaballooning.com/api/deleteBooking', { BOOKING_ID: bookingId });
            if (response.data.success) {
                message.success('Booking deleted successfully');
                this.getAllBookings();
            } else {
                message.error('Failed to delete booking');
            }
        } catch (error) {
            console.error('Error deleting booking:', error);
            message.error('Internal server error');
        }
    };

    groupBookingsByDate(bookings) {
        const groups = {};
        const { searchText, dateRange } = this.state;
        const [startDate, endDate] = dateRange;

        bookings.forEach((booking) => {
            const pass = booking.PASSENGER_NAME?.toLowerCase().includes(searchText);
            const guide = booking.AGENT?.toLowerCase().includes(searchText);
            const tour = booking.TOUR_NO?.toLowerCase().includes(searchText);
            const hotel = booking.HOTEL?.toLowerCase().includes(searchText);

            if (searchText && !(pass || guide || tour || hotel)) return;

            if (startDate) {
                const bookingDate = moment(booking.DATE, 'YYYY-MM-DD');
                if (bookingDate.isBefore(startDate, 'day')) return;
                if (endDate && bookingDate.isAfter(endDate, 'day')) return;
            }

            const dateKey = booking.DATE;
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(booking);
        });

        return Object.keys(groups).map((date) => {
            const sortedBookings = groups[date].sort((a, b) => (a.TOUR_NO || '').localeCompare(b.TOUR_NO || ''));
            const bookingsWithRowSpan = this.computeTourRowSpan(sortedBookings);
            return {
                bookingDate: date,
                bookings: bookingsWithRowSpan,
            };
        });
    }

    computeTourRowSpan(rows) {
        const newRows = [...rows];
        let i = 0;
        const lightColors = ['group-color-1', 'group-color-2'];
        let groupIndex = 0;

        while (i < newRows.length) {
            let count = 1;
            let paxSum = Number(newRows[i].PAX_NO) || 0;

            for (let j = i + 1; j < newRows.length; j++) {
                if (newRows[j].TOUR_NO === newRows[i].TOUR_NO) {
                    count++;
                    paxSum += Number(newRows[j].PAX_NO) || 0;
                } else {
                    break;
                }
            }

            const groupClass = lightColors[groupIndex % lightColors.length];

            for (let k = i; k < i + count; k++) {
                newRows[k].tourRowSpan = k === i ? count : 0;
                newRows[k].groupColorClass = groupClass;
                newRows[k].isFirstInTourGroup = k === i;
                newRows[k].tourPaxSum = paxSum;
            }

            i += count;
            groupIndex++;
        }

        return newRows;
    }

    render() {
        let rememberedUser = Cookies.get('rememberedUser');
        let ROLE = null;
        if (rememberedUser) {
            rememberedUser = JSON.parse(rememberedUser);
            ROLE = rememberedUser.ROLE;
        }
        if (ROLE === 'USER_MILL') {
            return <Redirect to="/dashboard" />;
        }

        const { bookings, loading, isUpdateBookingModalVisible, selectedBooking, dateRange } = this.state;
        const groupedBookings = this.groupBookingsByDate(bookings);

        const columns = [
            {
                title: 'Tour No',
                dataIndex: 'TOUR_NO',
                key: 'TOUR_NO',
                render: (text, row) => ({
                    children: `${text} (${row.tourRowSpan > 0 ? ` Total Pax: ${row.tourPaxSum}` : ''})`,
                    props: { rowSpan: row.tourRowSpan },
                }),
            },
            {
                title: 'Guide/Agent',
                dataIndex: 'AGENT',
                key: 'AGENT',
            },
            {
                title: 'Passenger Name',
                dataIndex: 'PASSENGER_NAME',
                key: 'PASSENGER_NAME',
            },
            {
                title: 'Contact',
                dataIndex: 'CONTACT',
                key: 'CONTACT',
            },
            {
                title: 'Hotel',
                dataIndex: 'HOTEL',
                key: 'HOTEL',
            },
            {
                title: 'Pax No',
                dataIndex: 'PAX_NO',
                key: 'PAX_NO',
            },
            {
                title: 'Remarks',
                dataIndex: 'REMARKS',
                key: 'REMARKS',
            },
        ];

        if (ROLE === 'ADMIN') {
            columns.push(
                {
                    title: 'Actions',
                    key: 'actions',
                    align: 'center',
                    render: (text, record) => (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => this.handleUpdateShow(record)}
                                style={{ marginRight: 8 }}
                            />
                            <Popconfirm
                                title="Are you sure you want to delete this booking?"
                                onConfirm={() => this.handleDelete(record.BOOKING_ID)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                        </>
                    ),
                },
                {
                    title: 'Created By',
                    dataIndex: 'CREATED_NAME',
                    key: 'CREATED_NAME',
                }
            );
        }

        return (
            <>
                <div className="tabled" style={{ padding: '20px', overflowX: 'auto' }}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                        <Col xs={24} md={6}><h2>Bookings</h2></Col>
                        <Col xs={12} sm={3}>
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Start Date"
                                value={dateRange[0]}
                                onChange={(date) => this.handleDateChange('start', date)}
                                allowClear
                            />
                        </Col>
                        <Col xs={12} sm={3}>
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="End Date"
                                value={dateRange[1]}
                                onChange={(date) => this.handleDateChange('end', date)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={6}>
                            <Button
                                style={{ width: '100%' }}
                                onClick={() => this.setState({ dateRange: [moment(), null] })}
                            >
                                Reset to Today
                            </Button>
                        </Col>
                        <Col xs={24} sm={24} md={6}>
                            <Input
                                placeholder="Search by Passenger, Guide, Tour No, or Hotel"
                                onChange={this.handleSearch}
                                allowClear
                            />
                        </Col>
                    </Row>
                    {loading ? (
                        <p>Loading bookings...</p>
                    ) : groupedBookings.length === 0 ? (
                        <p>No bookings available.</p>
                    ) : (
                        groupedBookings.map((group, index) => (
                            <div key={index} style={{ marginBottom: 40 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        color: '#fff',
                                        background: this.state.dailyStatuses[group.bookingDate]
                                            ? 'linear-gradient(90deg, #cf1322, #ff4d4f)' // Red gradient for cancelled
                                            : 'linear-gradient(90deg, #00936F, #F9A521)', // Original gradient
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}
                                >
                                    <span>Date: {group.bookingDate}</span>
                                    {ROLE === 'USER_VIEW' ? (
                                        this.state.dailyStatuses[group.bookingDate] ? (
                                            <span style={{ color: '#fff', fontWeight: 'bold' }}>Flight Cancelled ✅</span>
                                        ) : null
                                    ) : (
                                        <Checkbox
                                            checked={this.state.dailyStatuses[group.bookingDate] || false}
                                            onChange={(e) => this.handleStatusChange(group.bookingDate, e.target.checked)}
                                            style={{ color: '#fff', fontWeight: 'bold' }}
                                        >
                                            <span style={{ color: '#fff' }}>
                                                {this.state.dailyStatuses[group.bookingDate] ? 'Flight Cancelled ✅' : 'Flight Cancelled?'}
                                            </span>
                                        </Checkbox>
                                    )}
                                </div>
                                <Table
                                    columns={columns}
                                    dataSource={group.bookings}
                                    rowKey="BOOKING_ID"
                                    pagination={false}
                                    bordered
                                    scroll={{ x: 'max-content' }}
                                    rowClassName={(record) =>
                                        `${record.groupColorClass} ${record.isFirstInTourGroup ? 'first-tour-row' : ''}`
                                    }
                                />
                            </div>
                        ))
                    )}
                </div>

                <Modal
                    title="Update Booking"
                    visible={isUpdateBookingModalVisible}
                    onCancel={this.toggleUpdateBookingModal}
                    footer={null}
                    width={800}
                >
                    {selectedBooking && (
                        <UpdateEditBooking
                            key={selectedBooking.BOOKING_ID}
                            initialValues={selectedBooking}
                            onUpdate={this.getAllBookings.bind(this)}
                            onCancel={this.toggleUpdateBookingModal}
                        />
                    )}
                </Modal>

                <style>{`
                    .first-tour-row td { padding-top: 10px !important; }
                    .ant-table td { padding-top: 4px; padding-bottom: 4px; }
                    .ant-table table { border-collapse: separate !important; border-spacing: 0px; }
                    .group-color-1 td { background-color: #f0fff4; }
                    .group-color-2 td { background-color: #f0f9ff; }
                `}</style>
            </>
        );
    }
}

export default withRouter(BookingView);
